import { createSlice, createSelector } from '@reduxjs/toolkit';
import { ROLES } from '../../config/roles';

// Build storage key per-user so users don't see each other's notifications
const getStorageKey = () => {
  try {
    const usr = JSON.parse(localStorage.getItem('user'));
    return usr?.id ? `notifications_${usr.id}` : 'notifications_guest';
  } catch {
    return 'notifications_guest';
  }
};

// Helper function to safely get stored notifications and ALWAYS return an array
const getStoredNotifications = () => {
  try {
    const stored = localStorage.getItem(getStorageKey());
    if (!stored) return [];

    const parsed = JSON.parse(stored);
    // Ensure the parsed value is an array; otherwise return empty array
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('Error loading stored notifications:', error);
    // If corrupted, clear it to avoid endless errors
    localStorage.removeItem(getStorageKey());
    return [];
  }
};

// Normalize a single notification so the rest of the store always sees a
// consistent shape. The backend now exposes `seen` (server-side read flag);
// the frontend has historically used `read`. We align the two: if the server
// says `seen`, the notification is read. Local `read` (set by the user in this
// session) is preserved when the server has no opinion yet.
const normalizeNotification = (n, existingRead) => {
  // Prefer an explicit server `seen` flag; fall back to existing local read
  // state, then to any pre-set `read`, then default to unread.
  const read =
    typeof n.seen === 'boolean'
      ? n.seen
      : typeof existingRead === 'boolean'
        ? existingRead
        : n.read || false;

  return {
    ...n,
    read,
    receivedAt: n.receivedAt || new Date().toISOString(),
  };
};

const sortByCreatedDesc = (list) =>
  list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

// Retrieve once to avoid double‐parsing and guarantee consistent value
const storedNotifications = getStoredNotifications();

// Helper function to save notifications to localStorage.
// The list is kept sorted newest-first, so slice(0, N) keeps the NEWEST N
// notifications. (A previous slice(-N) kept the TAIL = oldest, which made the
// cache repaint stale rows and a wrong unread count on reload until hydration.)
const saveNotificationsToStorage = (notifications) => {
  try {
    // Limit to the newest 100 notifications to prevent storage bloat
    const limited = notifications.slice(0, 100);
    localStorage.setItem(getStorageKey(), JSON.stringify(limited));
  } catch (error) {
    console.error('Error saving notifications to storage:', error);
    // If storage quota exceeded, try to save fewer (still the newest) notifications
    try {
      const limited = notifications.slice(0, 50);
      localStorage.setItem(getStorageKey(), JSON.stringify(limited));
    } catch (retryError) {
      console.error('Failed to save notifications even with reduced count:', retryError);
    }
  }
};

// Initial state
const initialState = {
  notifications: storedNotifications,
  unreadCount: storedNotifications.filter(n => !n.read).length,
  // Server-side unread total (pagination.unread_count). The loaded list is
  // capped at one page (20), so counting it client-side undercounts; this is
  // the authoritative badge source once a hydrate delivered it. null until
  // the first server round-trip (client count is the fallback).
  serverUnreadCount: null,
  connectionStatus: 'disconnected', // 'connecting', 'connected', 'disconnected', 'reconnecting', 'error'
  isLoading: false,
  error: null,
  lastFetch: null,
  pollingEnabled: false
};

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    // Add a new notification (websocket arrival). Toast is fired by the
    // websocket handler, NOT here, so hydration never triggers a toast.
    addNotification: (state, action) => {
      const notification = normalizeNotification(
        { ...action.payload, seen: action.payload.seen ?? false },
        false
      );

      // Check for duplicates based on ID
      const exists = state.notifications.find(n => n.id === notification.id);
      if (!exists) {
        state.notifications.push(notification);
        if (!notification.read) {
          state.unreadCount += 1;
          if (typeof state.serverUnreadCount === 'number') {
            state.serverUnreadCount += 1;
          }
        }

        // Sort by created_at (newest first)
        sortByCreatedDesc(state.notifications);

        // Save to localStorage (cache only)
        saveNotificationsToStorage(state.notifications);
      }
    },

    // Hydrate from server history on mount. Merges the server list with any
    // notifications already in the store (e.g. websocket arrivals that landed
    // before hydration finished), dedups by id, aligns read<->seen, sorts
    // newest-first. Does NOT fire a toast (server history is not "new").
    hydrateNotifications: (state, action) => {
      // Payload is either the plain server list (legacy callers) or
      // { list, unreadCount } where unreadCount is the server-side total
      // (pagination.unread_count) that feeds the badge beyond the page cap.
      const payload = action.payload || {};
      const serverList = Array.isArray(payload)
        ? payload
        : Array.isArray(payload.list)
          ? payload.list
          : [];
      const serverUnread = Array.isArray(payload) ? undefined : payload.unreadCount;
      if (typeof serverUnread === 'number' && serverUnread >= 0) {
        state.serverUnreadCount = serverUnread;
      }

      // Index existing notifications so we can preserve any local read state
      // the user set this session before the server round-trip completed.
      const existingById = new Map(
        state.notifications.map((n) => [n.id, n])
      );

      const merged = new Map();

      // Server is the source of truth for membership + seen state.
      serverList.forEach((raw) => {
        const existing = existingById.get(raw.id);
        merged.set(
          raw.id,
          normalizeNotification(raw, existing ? existing.read : undefined)
        );
      });

      // Keep any in-store notifications not present in the server page
      // (e.g. a websocket arrival newer than the fetched page).
      state.notifications.forEach((n) => {
        if (!merged.has(n.id)) {
          merged.set(n.id, n);
        }
      });

      state.notifications = sortByCreatedDesc(Array.from(merged.values()));
      state.unreadCount = state.notifications.filter((n) => !n.read).length;
      state.lastFetch = new Date().toISOString();

      // Save to localStorage (cache only)
      saveNotificationsToStorage(state.notifications);
    },
    
    // Set multiple notifications (from API fetch)
    setNotifications: (state, action) => {
      state.notifications = action.payload.map((notification) =>
        normalizeNotification(notification)
      );

      // Sort by created_at (newest first)
      sortByCreatedDesc(state.notifications);

      state.unreadCount = state.notifications.filter(n => !n.read).length;
      state.lastFetch = new Date().toISOString();
      
      // Save to localStorage
      saveNotificationsToStorage(state.notifications);
    },
    
    // Mark a specific notification as read
    markAsRead: (state, action) => {
      const notificationId = action.payload;
      const notification = state.notifications.find(n => n.id === notificationId);
      
      if (notification && !notification.read) {
        notification.read = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
        if (typeof state.serverUnreadCount === 'number') {
          state.serverUnreadCount = Math.max(0, state.serverUnreadCount - 1);
        }

        // Save to localStorage
        saveNotificationsToStorage(state.notifications);
      }
    },

    // Mark all notifications as read
    markAllAsRead: (state) => {
      state.notifications.forEach(notification => {
        notification.read = true;
      });
      state.unreadCount = 0;
      // The backend PATCH marks ALL rows seen (beyond the loaded page), so
      // the server total drops to zero with it.
      state.serverUnreadCount = typeof state.serverUnreadCount === 'number' ? 0 : state.serverUnreadCount;

      // Save to localStorage
      saveNotificationsToStorage(state.notifications);
    },
    
    // Remove a specific notification
    removeNotification: (state, action) => {
      const notificationId = action.payload;
      const index = state.notifications.findIndex(n => n.id === notificationId);
      
      if (index !== -1) {
        const wasUnread = !state.notifications[index].read;
        state.notifications.splice(index, 1);

        if (wasUnread) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
          if (typeof state.serverUnreadCount === 'number') {
            state.serverUnreadCount = Math.max(0, state.serverUnreadCount - 1);
          }
        }
        
        // Save to localStorage
        saveNotificationsToStorage(state.notifications);
      }
    },
    
    // Clear all notifications
    clearNotifications: (state) => {
      state.notifications = [];
      state.unreadCount = 0;
      // Backend Clear-All dismisses every row, so the server total is zero.
      state.serverUnreadCount = typeof state.serverUnreadCount === 'number' ? 0 : state.serverUnreadCount;

      // Clear from localStorage
      localStorage.removeItem(getStorageKey());
    },
    
    // Set connection status. Must NOT touch pollingEnabled: the polling
    // fallback (useNotificationPolling, mounted by WebSocketManager) runs
    // exactly when pollingEnabled && the socket is down — forcing the flag
    // false here is what made the fallback dead code and the badge stale
    // whenever the on-demand websocket was closed.
    setConnectionStatus: (state, action) => {
      console.log('setConnectionStatus', action.payload);

      state.connectionStatus = action.payload;
    },
    
    // Set loading state
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    
    // Set error state
    setError: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    
    // Clear error state
    clearError: (state) => {
      state.error = null;
    },
    
    // Update last fetch timestamp
    updateLastFetch: (state) => {
      state.lastFetch = new Date().toISOString();
    },
    
    // Toggle polling
    setPollingEnabled: (state, action) => {
      state.pollingEnabled = action.payload;
    },
    
    // Cleanup old notifications (older than 30 days)
    cleanupOldNotifications: (state) => {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const initialLength = state.notifications.length;
      state.notifications = state.notifications.filter(notification => 
        new Date(notification.created_at) > thirtyDaysAgo
      );
      
      // Recalculate unread count
      state.unreadCount = state.notifications.filter(n => !n.read).length;
      
      // Save to localStorage if cleaned up
      if (state.notifications.length !== initialLength) {
        saveNotificationsToStorage(state.notifications);
        console.log(`Cleaned up ${initialLength - state.notifications.length} old notifications`);
      }
    }
  }
});

// Export actions
export const {
  addNotification,
  hydrateNotifications,
  setNotifications,
  markAsRead,
  markAllAsRead,
  removeNotification,
  clearNotifications,
  setConnectionStatus,
  setLoading,
  setError,
  clearError,
  updateLastFetch,
  setPollingEnabled,
  cleanupOldNotifications
} = notificationsSlice.actions;

// Selectors
export const selectAllNotifications = (state) => state.notifications.notifications;
export const selectUnreadNotifications = (state) => 
  state.notifications.notifications.filter(n => !n.read);
export const selectUnreadCount = (state) => state.notifications.unreadCount;
export const selectConnectionStatus = (state) => state.notifications.connectionStatus;
export const selectIsLoading = (state) => state.notifications.isLoading;
export const selectError = (state) => state.notifications.error;
export const selectLastFetch = (state) => state.notifications.lastFetch;
export const selectPollingEnabled = (state) => state.notifications.pollingEnabled;

// Complex selectors
export const selectNotificationsByCategory = (state, category) =>
  state.notifications.notifications.filter(n => n.category === category);

export const selectRecentNotifications = (state, hours = 24) => {
  const cutoff = new Date();
  cutoff.setHours(cutoff.getHours() - hours);
  return state.notifications.notifications.filter(n => 
    new Date(n.created_at) > cutoff
  );
};

export const selectIsConnected = (state) => 
  state.notifications.connectionStatus === 'connected';

export const selectIsReconnecting = (state) => 
  state.notifications.connectionStatus === 'reconnecting';

export const selectHasError = (state) =>
  state.notifications.connectionStatus === 'error' || !!state.notifications.error;

// --- Perspective filter (D-011) --------------------------------------------
// In the AgencyAdmin (company) perspective the personal channels Messages and
// Meetings are hidden from the bell, dropdown, sidebar and /notifications list
// and excluded from the unread badge. Every other perspective (Agent /
// personal) sees everything. Kept as ONE role-aware selector so the four
// surfaces cannot drift, and it recomputes live when the role is switched.
const AGENCY_HIDDEN_CATEGORIES = new Set(['messages', 'meetings']);

// The DB Notifications.category FK stores LEGACY display names, not the
// preference keys (backend CATEGORY_DB_NAME in notifications/dispatch.py):
// three of them do not lowercase into their key — 'Transfer Windows',
// 'Coaching Personality' and the odd 'player_detailed_position_update' row
// that the positions category writes. This map (keyed lowercase) brings every
// legacy DB name back to its canonical preference key so the category
// classifier speaks ONE vocabulary regardless of which shape a row arrived
// in. The realtime websocket payload sends the preference key itself as a
// plain string (dispatch.py _create_in_app payload['category']), the REST
// serializer sends the object with the DB name — both normalize here.
const DB_NAME_TO_CATEGORY_KEY = {
  tenders: 'tenders',
  pitches: 'pitches',
  messages: 'messages',
  meetings: 'meetings',
  mandate: 'mandate',
  player_detailed_position_update: 'positions',
  positions: 'positions',
  invites: 'invites',
  'transfer windows': 'transfer_windows',
  transfer_windows: 'transfer_windows',
  agency: 'agency',
  scouting: 'scouting',
  'coaching personality': 'coaching_personality',
  coaching_personality: 'coaching_personality',
};

// Map a notification to its preference-category key. Prefers the server
// category (object with category_name from REST, plain key string from the
// websocket), normalized to the canonical preference key; falls back to the
// notification_type for rows that carry no category at all. An unknown
// category name still returns a stable lowercased bucket rather than '' so a
// future category is never invisible to category-driven surfaces.
export const notificationCategoryKey = (n) => {
  const raw =
    typeof (n && n.category) === 'string'
      ? n.category
      : n && n.category && n.category.category_name;
  if (raw) {
    const lower = String(raw).toLowerCase();
    return DB_NAME_TO_CATEGORY_KEY[lower] || lower;
  }
  const type = (n && n.notification_type) || '';
  if (type === 'chat_message') return 'messages';
  if (type.startsWith('meeting_')) return 'meetings';
  return '';
};

export const isNotificationVisibleForRole = (notification, currentRole) => {
  if (currentRole !== ROLES.AgencyAdmin) return true;
  return !AGENCY_HIDDEN_CATEGORIES.has(notificationCategoryKey(notification));
};

const selectCurrentRole = (state) => state.auth && state.auth.currentRole;

// Role-aware visible list + unread count (memoized so all surfaces share one
// filtered array reference and re-render together on a role switch).
export const selectVisibleNotifications = createSelector(
  [selectAllNotifications, selectCurrentRole],
  (notifications, currentRole) =>
    notifications.filter((n) => isNotificationVisibleForRole(n, currentRole)),
);

const selectServerUnreadCount = (state) => state.notifications.serverUnreadCount;

// Badge count. Once the server delivered its authoritative total
// (pagination.unread_count — counts ALL unseen rows, beyond the 20-row page
// cap), that total drives the badge; the loaded-list count is only the
// fallback before the first hydrate. For the AgencyAdmin perspective (D-011)
// the server total does not know about hidden categories, so the locally
// known hidden unread rows are subtracted; hidden rows beyond the loaded page
// cannot be known and are accepted as over-count rather than silently capping
// the badge at 20 again. Never drops below the locally visible unread count.
export const selectVisibleUnreadCount = createSelector(
  [selectAllNotifications, selectCurrentRole, selectServerUnreadCount],
  (notifications, currentRole, serverUnreadCount) => {
    const visibleUnread = notifications.filter(
      (n) => !n.read && isNotificationVisibleForRole(n, currentRole)
    ).length;
    if (typeof serverUnreadCount !== 'number') return visibleUnread;
    const hiddenUnreadLoaded = notifications.filter(
      (n) => !n.read && !isNotificationVisibleForRole(n, currentRole)
    ).length;
    return Math.max(serverUnreadCount - hiddenUnreadLoaded, visibleUnread);
  },
);

export default notificationsSlice.reducer; 